import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../store/store";
import { api } from "../services/api";
import { updateMiqCollection } from "wasp/client/operations";
import { timeAgo } from "../admin/dashboards/utils";
import { setReconnectDialogOpen } from "../store/flowSlice";

type UseSyncShopifyProps = {
  onCompleted?: () => void;
};

type UseSyncShopifyHook = {
  syncShopify: (onComplete?: () => void) => Promise<void>;
  isSyncing: boolean;
  progress: number;
  error: string | null;
  lastSync: string;
};

export const useSyncShopify = ({
  onCompleted,
}: UseSyncShopifyProps): UseSyncShopifyHook => {
  const { currentCollection, currentUser } = useSelector(
    (state: RootState) => state.user
  );
  const dispatch = useDispatch();
  const [error, setError] = useState<string | null>(null);
  const [syncJobId, setSyncJobId] = useState<string | null>(null);
  const [lastSync, setLastSync] = useState("");
  const [fnOnCompleted, setFnOnCompleted] = useState<(() => void) | null>(null);

  useEffect(() => {
    if (currentCollection?.collection?.downsyncJobId != null) {
      setSyncJobId(currentCollection.collection?.downsyncJobId);
      setIsSyncing(true);
    } else {
      setSyncJobId(null);
      setIsSyncing(false);
    }

    if(currentCollection?.collection?.lastDownsyncEndedAt){
      setLastSync(timeAgo(currentCollection.collection?.lastDownsyncEndedAt));
    }
  }, [currentCollection]);

  const reset = async () => {
    setProgress(0);
    setIsSyncing(false);
    if(currentCollection?.collection?.id){
      await updateMiqCollection({
        id: currentCollection?.collection.id,
        lastDownsyncEndedAt: new Date(),
        downsyncJobId: null,
      });
    }
   
  };

  const fullReset = () => {
    reset();
    setError(null);
  };

  const [isSyncing, setIsSyncing] = useState(false);
  const [progress, setProgress] = useState(0);

  const pollJobStatus = async (jobId: string, interval: number = 1000): Promise<{ completed: boolean; result: any }> => {
    try {
      const response = await api.getJobStatus(jobId);

      if (response.error) {
        throw new Error(response.error.message);
      }

      if (!response.data) {
        throw new Error("No data found in response");
      }

      const { progress: jobProgress, response: result, status } = response.data;

      setProgress(jobProgress);

      if (status === "completed") {
        return { completed: true, result };
      }

      if (status === "failed") {
        throw new Error(response.data?.message);
      }

      // Continue polling
      await new Promise((resolve) => setTimeout(resolve, interval));
      return pollJobStatus(jobId, interval);
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Unknown error occurred"
      );
    }
  };

  useEffect(() => {
    if (!syncJobId) {
      return;
    }
    const handleJobCompletion = async () => {
      try {
        const { completed, result } = await pollJobStatus(syncJobId);
        if(!currentCollection?.collection?.id){
          throw new Error("Collection ID is missing");
        }
        const {stats} = result;
        if(completed){
          await updateMiqCollection({
            id: currentCollection.collection?.id,
            lastDownsyncEndedAt: new Date(),
            downsyncJobId: null,
            stats: JSON.stringify(stats),
          });
          setLastSync("Just now");
          fnOnCompleted?.();
          onCompleted?.();
          fullReset();
        } else{
          setError("Failed to sync Shopify. Couldn't complete the job.");
          reset();
        }
      } catch (err) {
        if((err as Error)?.message?.includes("401: Unauthorized")){
          dispatch(setReconnectDialogOpen(true));
        }
        setError(err instanceof Error ? err.message : "Failed to sync Shopify");
        reset();
      }
    };
    handleJobCompletion();
  }, [syncJobId]);

  const syncShopify = async (onComplete?: () => void) => {
    if(onComplete){
      setFnOnCompleted(onComplete);
    }
    fullReset();
    try {
      setIsSyncing(true);
      if (!currentUser?.email) {
        throw new Error("User email is missing. Cannot proceed with sync.");
      }
      if (!currentCollection?.collection?.miqShopifyDomain) {
        throw new Error("Shop domain is missing. Cannot proceed with sync.");
      }
      if (!currentCollection?.collection?.name) {
        throw new Error(
          "Collection name is missing. Cannot proceed with sync."
        );
      }

      const { data, success, error } = await api.postShopifyToInternalTable({
        account_id: currentUser?.email,
        shopify_store_name: currentCollection?.collection?.miqShopifyDomain,
        collection_name: currentCollection?.collection?.name,
      });

      if (!success) {
        throw new Error(
          error?.message || "Couldn't start import job. Please try again later."
        );
      }

      if (!data?.job_id) {
        throw new Error("Couldn't get job id for import job.");
      }

      await updateMiqCollection({
        id: currentCollection.collection?.id,
        downsyncJobId: data.job_id,
        lastDownsyncStartedAt: new Date(),
      });

    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to sync Shopify");
      reset();
    }
  };

  return {
    syncShopify,
    isSyncing,
    progress,
    error,
    lastSync
  };
};
